import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import {
  selectAgentConversationFormPendingUserFileUploads,
  selectAgentConversationFormUserFileIds,
  selectAgentConversationFormWritingSampleFileIds,
} from '@/agent-v2/chat-form/store/selectors'
import { splitChipsIntoRows } from '@/agent-v2/utils/split-chips-into-rows'
import { useOpenChatAttachedFile } from '@/agent-v2/hooks/useOpenChatAttachedFile'
import { getUserFileAttachmentStatus, type UserFileAttachmentStatus } from '@/chat-common/selected_files/utils/user-file-upload-status'
import { FileChip, type FileChipStatus } from '@/components/file-chip/FileChip'
import { FileUploadContext } from '@/context/file-upload-context'
import { selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { canOpenAttachedFile, kDeletedFileName } from '@/agent-v2/utils/chat-attached-file'

// Matches the rows' gap-2
const kChipGapPx = 8

type ComposerChip = {
  key: string
  name: string
  isWritingSample: boolean
  status?: FileChipStatus
  errorMessage?: string
  disabled?: boolean
  onOpen?: () => void
  onRemove: () => void
}

const toChipStatus = (status: UserFileAttachmentStatus): FileChipStatus | undefined =>
  status === 'uploading' || status === 'processing' ? 'processing' : status === 'error' ? 'error' : undefined

const isInFlight = (uploadState?: FileUploadState) =>
  !!uploadState && ['validating', 'queued', 'initializing', 'uploading'].includes(uploadState.client.uploadStatus)

/** The files waiting in the composer to go out with the next message, each removable before it's sent. */
export function ComposerFileChips({ conversationId, onLastFileRemoved }: { conversationId: string; onLastFileRemoved?: () => void }) {
  const dispatch = useAppDispatch()
  const openChatAttachedFile = useOpenChatAttachedFile(conversationId)
  const { cancelUserFileUpload } = useContext(FileUploadContext)

  const userFileIds = useAppSelector((state: RootState) => selectAgentConversationFormUserFileIds(state, { conversationId }))
  const writingSampleFileIds = useAppSelector((state: RootState) => selectAgentConversationFormWritingSampleFileIds(state, { conversationId }))
  const { filesById, unavailableIds, error: fetchError } = useUserFiles([...userFileIds, ...writingSampleFileIds])
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)
  const pendingUploads = useAppSelector((state: RootState) => selectAgentConversationFormPendingUserFileUploads(state, { conversationId }))

  const toFileChip = (fileId: string, isWritingSample: boolean): ComposerChip => {
    const key = `${isWritingSample ? 'writing-sample' : 'file'}:${fileId}`
    const uploadState = uploadFilesByServerFileId[fileId]
    const onRemove = () => {
      if (isInFlight(uploadState)) cancelUserFileUpload(uploadState!.client.clientTmpId)
      dispatch(
        isWritingSample
          ? AgentConversationFormActions.removeWritingSampleFile({ conversationId, userFileId: fileId })
          : AgentConversationFormActions.removeUserFile({ conversationId, userFileId: fileId })
      )
    }

    const file = uploadState?.backend?.record ?? filesById[fileId]
    if (!file && !uploadState && unavailableIds.includes(fileId)) return { key, name: kDeletedFileName, isWritingSample, disabled: true, onRemove }

    const attachmentStatus = getUserFileAttachmentStatus({ file, uploadState, loadError: fetchError && !file })
    const status = toChipStatus(attachmentStatus.status)
    const canOpen = !status && !!file && canOpenAttachedFile(file)
    return {
      key,
      name: file?.original_filename ?? uploadState?.client.filename ?? fileId,
      isWritingSample,
      status,
      errorMessage: attachmentStatus.errorMessage,
      onOpen: canOpen ? () => openChatAttachedFile(file) : undefined,
      onRemove,
    }
  }

  // Uploads in flight show in the slot they'll land in until the server id arrives
  const pendingChips = pendingUploads.flatMap(({ target, uploadState }): ComposerChip[] => {
    if (!uploadState) return []
    const attachmentStatus = getUserFileAttachmentStatus({ uploadState })
    return [
      {
        key: uploadState.client.clientTmpId,
        name: uploadState.client.filename,
        isWritingSample: target === 'writing-sample',
        status: toChipStatus(attachmentStatus.status),
        errorMessage: attachmentStatus.errorMessage,
        onRemove: () => {
          cancelUserFileUpload(uploadState.client.clientTmpId)
          dispatch(AgentConversationFormActions.removePendingUserFileUpload({ conversationId, clientTmpId: uploadState.client.clientTmpId }))
        },
      },
    ]
  })

  // The writing sample leads, and each upload sits at the end of its group, so a finished upload joins its group in place
  const chips = [
    ...writingSampleFileIds.map((fileId) => toFileChip(fileId, true)),
    ...pendingChips.filter((chip) => chip.isWritingSample),
    ...userFileIds.map((fileId) => toFileChip(fileId, false)),
    ...pendingChips.filter((chip) => !chip.isWritingSample),
  ]

  const containerRef = useRef<HTMLDivElement>(null)
  const rowsRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const removedChipIndexRef = useRef<number | null>(null)
  const [rows, setRows] = useState<number[][]>([chips.map((_, index) => index)])
  const chipsKey = chips.map((chip) => `${chip.key}:${chip.name}:${chip.status ?? ''}`).join('|')

  const hasNoChips = chips.length === 0

  // A removed chip takes its focus with it, so focus moves to the chip that took its place, or the one before it. Runs
  // once the rows have re-split, since that can move chips into the other row and remount them.
  useLayoutEffect(() => {
    const removedChipIndex = removedChipIndexRef.current
    if (removedChipIndex === null) return
    removedChipIndexRef.current = null

    const dismissButtons = rowsRef.current?.querySelectorAll<HTMLElement>('[data-chip-dismiss]') ?? []
    const nextDismissButton = dismissButtons[Math.min(removedChipIndex, dismissButtons.length - 1)]
    if (nextDismissButton) nextDismissButton.focus()
    else onLastFileRemoved?.()
  }, [rows, hasNoChips, onLastFileRemoved])

  // Measures every chip at its natural width in the hidden copy, then splits them into one or two rows
  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const update = () => {
      const chipWidths = [...measure.children].map((child) => child.getBoundingClientRect().width)
      setRows(splitChipsIntoRows({ chipWidths, containerWidth: container.clientWidth, gap: kChipGapPx }))
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(container)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [chipsKey])

  if (chips.length === 0) return null

  const renderChip = (chip: ComposerChip, index: number) => (
    <FileChip
      key={chip.key}
      name={chip.name}
      variant={chip.isWritingSample ? 'template' : 'file'}
      status={chip.status}
      errorMessage={chip.errorMessage}
      disabled={chip.disabled}
      onClick={chip.onOpen}
      onDismiss={() => {
        removedChipIndexRef.current = index
        chip.onRemove()
      }}
    />
  )

  return (
    <div ref={containerRef} className="relative mb-1">
      {/* Runs edge to edge across the input (whose padding is --agent-v2-input-padding) so chips scroll under its padding.
          The bottom padding keeps the scrollbar clear of the second row. */}
      <div className="-mx-[var(--agent-v2-input-padding)] overflow-x-auto px-[var(--agent-v2-input-padding)] pb-2">
        <div ref={rowsRef} className="flex w-max flex-col gap-2">
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-2">
              {row.flatMap((index) => (chips[index] ? [renderChip(chips[index], index)] : []))}
            </div>
          ))}
        </div>
      </div>
      {/* Clipped to nothing, so the full-width copy never widens the page */}
      <div aria-hidden className="invisible absolute left-0 top-0 size-0 overflow-hidden">
        <div ref={measureRef} className="flex w-max gap-2">
          {chips.map(renderChip)}
        </div>
      </div>
    </div>
  )
}
